import { forwardRef, InputHTMLAttributes } from "react";
import { styled } from "styled-components";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  error?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input<{ $hasPrefix?: boolean; $hasSuffix?: boolean; $hasError?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  padding-left: ${({ $hasPrefix }) => ($hasPrefix ? "40px" : "12px")};
  padding-right: ${({ $hasSuffix }) => ($hasSuffix ? "40px" : "12px")};
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.blue900};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, $hasError }) => ($hasError ? "#ff4d4f" : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) => ($hasError ? "#ff4d4f" : theme.colors.primary)};
    box-shadow: 0 0 0 2px ${({ theme, $hasError }) => ($hasError ? "rgba(255, 77, 79, 0.2)" : "rgba(246, 163, 28, 0.2)")};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.grey100};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.lightGrey};
  }
`;

const PrefixIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.grey};
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SuffixIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.grey};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 4px;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, prefix, suffix, ...props }, ref) => {
    return (
      <InputWrapper>
        {prefix && <PrefixIcon>{prefix}</PrefixIcon>}
        <StyledInput
          ref={ref}
          $hasPrefix={!!prefix}
          $hasSuffix={!!suffix}
          $hasError={!!error}
          {...props}
        />
        {suffix && <SuffixIcon>{suffix}</SuffixIcon>}
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </InputWrapper>
    );
  }
);

Input.displayName = "Input";
